import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form/form';
import { Input } from '@/components/ui/form/input';
import { cn, UserRequestCreate, useSignUpForm } from '@liquidbudget/common';
import { CheckIcon, LockIcon } from 'lucide-react';
import { Control } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';

export const SignUpForm = () => {
  const navigate = useNavigate();
  const onSuccess = () => navigate('/dashboard/budget-manager', { replace: true });
  const { form, isSubmitting, onSubmit } = useSignUpForm({ onSuccess });

  return (
    <div className={cn('flex flex-col gap-6')}>
      <Card className="overflow-hidden">
        <CardContent className="grid p-0 md:grid-cols-2">
          <Form {...form}>
            <form onSubmit={onSubmit} className="space-y-8 p-6 md:p-8">
              <div className="flex flex-col gap-6">
                <div className="flex flex-col items-center text-center">
                  <h1 className="text-4xl font-bold">Sign Up</h1>
                  <p className="text-balance text-muted-foreground mt-2">No credit card required</p>
                </div>
                <div className="grid gap-2">
                  <EmailField control={form.control} />
                </div>
                <div className="grid gap-2">
                  <PasswordField control={form.control} />
                </div>
                <Button type="submit" variant="sky" className="w-full mt-4 text-base" isLoading={isSubmitting}>
                  Let's Go
                </Button>
                <div className="-mt-3 text-balance text-center text-xs text-muted-foreground [&_a]:underline [&_a]:underline-offset-4 hover:[&_a]:text-primary">
                  By signing up, you agree to our <Link to="/terms">Terms of Service</Link> and{' '}
                  <Link to="/privacy-policy">Privacy Policy</Link>.
                </div>
                <div className="mt-2 text-center text-md text-muted-foreground">
                  Already have an account?{' '}
                  <Link to="/login" className="ml-1 font-medium underline underline-offset-4 hover:text-primary">
                    Log in
                  </Link>
                </div>
              </div>
            </form>
          </Form>
          <div className="relative bg-accent !border-none">
            <div className="p-6 md:p-8">
              <div className="flex flex-col gap-8">
                <div className="flex flex-col gap-6">
                  <div className="flex flex-col items-center text-center">
                    <h1 className="text-4xl font-bold"> Just $5/Month</h1>
                    <p className="text-balance text-muted-foreground mt-2">Join today and start saving</p>
                  </div>
                </div>

                {/* Features Section */}
                <div className="space-y-6">
                  <ul className="space-y-3">
                    {[
                      '45-day free trial',
                      'Bank integration included',
                      'Cancel anytime, no hassle',
                      'Built for desktop, tablet, and mobile',
                    ].map((feature) => (
                      <li key={feature} className="flex items-center gap-3">
                        <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full bg-accent-foreground/10">
                          <CheckIcon className="w-3.5 h-3.5" />
                        </div>
                        <span className="text-lg font-medium">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Value Proposition */}
                  <div className="space-y-6 pt-4 border-t border-accent-foreground/10">
                    <p className="text-base leading-relaxed">
                      Most users
                      <span className="font-medium "> save 10x the cost of their subscription</span> by staying in
                      control of their finances.
                    </p>

                    <div className="bg-accent-foreground/5 rounded-lg p-4">
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0 p-2 bg-accent-foreground/10 rounded-full">
                          <LockIcon />
                        </div>
                        <p className="text-base leading-relaxed">
                          Your data is safe with industry-standard encryption and will never be sold.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const EmailField = ({ control }: { control: Control<UserRequestCreate> }) => (
  <FormField
    control={control}
    name="email"
    render={({ field }) => (
      <FormItem>
        <FormLabel>Email</FormLabel>
        <FormControl>
          <Input id="email" type="email" required {...field} />
        </FormControl>
        <FormMessage />
      </FormItem>
    )}
  />
);

const PasswordField = ({ control }: { control: Control<UserRequestCreate> }) => (
  <FormField
    control={control}
    name="password"
    render={({ field }) => (
      <FormItem>
        <div className="flex items-center">
          <FormLabel>Password</FormLabel>
        </div>
        <FormControl>
          <Input id="password" type="password" required {...field} />
        </FormControl>
        <FormMessage />
      </FormItem>
    )}
  />
);
